/**
 * PortalSecurity — 2FA / MFA management page
 *
 * Uses Supabase Auth's built-in TOTP MFA support.
 * Users can enroll, verify, and unenroll TOTP factors.
 */

import React, { useState, useEffect, useCallback } from 'react';
import PortalLayout from '@/components/portal/PortalLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Shield, ShieldCheck, ShieldOff, Loader2, Copy, Check } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

type MfaState = 'loading' | 'not_enrolled' | 'pending_verification' | 'enrolled';

export default function PortalSecurity() {
  const { user } = useAuth();
  const [mfaState, setMfaState] = useState<MfaState>('loading');
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [factorId, setFactorId] = useState<string | null>(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const checkMfaStatus = useCallback(async () => {
    try {
      const { data, error: err } = await supabase.auth.mfa.listFactors();
      if (err) throw err;

      const totpFactors = data?.totp || [];
      const verifiedFactor = totpFactors.find(f => f.status === 'verified');
      const unverifiedFactor = totpFactors.find(f => f.status === 'unverified');

      if (verifiedFactor) {
        setFactorId(verifiedFactor.id);
        setMfaState('enrolled');
      } else if (unverifiedFactor) {
        setFactorId(unverifiedFactor.id);
        setMfaState('pending_verification');
      } else {
        setMfaState('not_enrolled');
      }
    } catch (err: any) {
      console.error('Failed to check MFA status:', err);
      setMfaState('not_enrolled');
    }
  }, []);

  useEffect(() => { checkMfaStatus(); }, [checkMfaStatus]);

  const handleEnroll = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: 'IntentDrive Authenticator',
      });
      if (err) throw err;

      setQrCode(data.totp.qr_code);
      setSecret(data.totp.secret);
      setFactorId(data.id);
      setMfaState('pending_verification');
    } catch (err: any) {
      setError(err.message || 'Failed to enroll MFA');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!factorId || verifyCode.length !== 6) return;
    setLoading(true);
    setError(null);
    try {
      const { data: challenge, error: challengeErr } = await supabase.auth.mfa.challenge({ factorId });
      if (challengeErr) throw challengeErr;

      const { error: verifyErr } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challenge.id,
        code: verifyCode,
      });
      if (verifyErr) throw verifyErr;

      setMfaState('enrolled');
      setQrCode(null);
      setSecret(null);
      setVerifyCode('');
      setSuccess('Two-factor authentication enabled successfully!');
    } catch (err: any) {
      setError(err.message || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleUnenroll = async () => {
    if (!factorId) return;
    setLoading(true);
    setError(null);
    try {
      const { error: err } = await supabase.auth.mfa.unenroll({ factorId });
      if (err) throw err;

      setMfaState('not_enrolled');
      setFactorId(null);
      setSuccess('Two-factor authentication disabled.');
    } catch (err: any) {
      setError(err.message || 'Failed to disable MFA');
    } finally {
      setLoading(false);
    }
  };

  const copySecret = () => {
    if (secret) {
      navigator.clipboard.writeText(secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <PortalLayout>
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Shield className="h-6 w-6 text-emerald-400" />
            Security
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Manage two-factor authentication and security settings.
          </p>
        </div>

        {error && (
          <div className="px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/30 text-sm text-red-400">
            {error}
          </div>
        )}
        {success && (
          <div className="px-4 py-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-sm text-emerald-400">
            {success}
          </div>
        )}

        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-base text-white flex items-center gap-2">
              {mfaState === 'enrolled' ? (
                <ShieldCheck className="h-5 w-5 text-emerald-400" />
              ) : (
                <ShieldOff className="h-5 w-5 text-slate-500" />
              )}
              Two-Factor Authentication (2FA)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {mfaState === 'loading' && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 text-emerald-400 animate-spin" />
              </div>
            )}

            {mfaState === 'not_enrolled' && (
              <>
                <p className="text-sm text-slate-400">
                  Add an extra layer of security to your account. You'll need an authenticator app
                  like Google Authenticator, Authy, or 1Password.
                </p>
                <Button onClick={handleEnroll} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Shield className="h-4 w-4 mr-2" />}
                  Enable 2FA
                </Button>
              </>
            )}

            {mfaState === 'pending_verification' && (
              <>
                <p className="text-sm text-slate-400">
                  Scan this QR code with your authenticator app, then enter the 6-digit code below.
                </p>
                {qrCode && (
                  <div className="flex justify-center py-4">
                    <img src={qrCode} alt="QR Code" className="w-48 h-48 rounded-lg" />
                  </div>
                )}
                {secret && (
                  <div className="flex items-center gap-2 bg-slate-900 rounded-lg px-3 py-2">
                    <code className="text-xs text-slate-300 flex-1 font-mono">{secret}</code>
                    <button onClick={copySecret} className="text-slate-400 hover:text-white">
                      {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
                    </button>
                  </div>
                )}
                <div className="flex gap-2">
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="000000"
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 text-white text-center text-lg font-mono tracking-widest"
                  />
                  <Button
                    onClick={handleVerify}
                    disabled={loading || verifyCode.length !== 6}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verify'}
                  </Button>
                </div>
              </>
            )}

            {mfaState === 'enrolled' && (
              <>
                <div className="flex items-center gap-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                  <ShieldCheck className="h-5 w-5 text-emerald-400 shrink-0" />
                  <div>
                    <div className="text-sm font-medium text-emerald-400">2FA is enabled</div>
                    <div className="text-xs text-slate-400">Your account is protected with two-factor authentication.</div>
                  </div>
                </div>
                <Button
                  variant="outline"
                  onClick={handleUnenroll}
                  disabled={loading}
                  className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ShieldOff className="h-4 w-4 mr-2" />}
                  Disable 2FA
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        {/* GDPR Section */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-base text-white">Data Privacy (GDPR)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-slate-400">
              Request deletion of all personal data associated with your account or specific sessions.
              This action is irreversible.
            </p>
            <div className="text-xs text-slate-500">
              To request data deletion, contact support or use the GDPR API endpoint: <code className="text-emerald-400">POST /functions/v1/gdpr-delete</code>
            </div>
          </CardContent>
        </Card>
      </div>
    </PortalLayout>
  );
}
